import { useState } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import { EmptyState, PageSpinner, Section } from "@impero-com/ui";
import { cx } from "class-variance-authority";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";

import { formatRangeLabel } from "./activityTrendLabels";
import { ControlHealthActivitiesWidget } from "./ControlHealthActivitiesWidget";
import { ControlHealthActivityTrendWidget } from "./ControlHealthActivityTrendWidget";
import { ControlHealthAdjustView } from "./ControlHealthAdjustView";
import { ControlHealthCompletionRateWidget } from "./ControlHealthCompletionRateWidget";
import * as theme from "./ControlHealthContainer.css";
import { ControlHealthFrictionBreakdownWidget } from "./ControlHealthFrictionBreakdownWidget";
import { ControlHealthFrictionRateWidget } from "./ControlHealthFrictionRateWidget";
import { ControlHealthOnTimeRateWidget } from "./ControlHealthOnTimeRateWidget";
import { ControlHealthStatusBreakdownTable } from "./ControlHealthStatusBreakdownTable";
import { ControlHealthWidgetsErrorBoundary } from "./ControlHealthWidgetsErrorBoundary";
import { ControlHealthFilterPanel } from "./filters/ControlHealthFilterPanel";
import {
    ControlHealthFiltersProvider,
    useControlHealthFilters,
} from "./filters/ControlHealthFiltersProvider";
import {
    DEFAULT_OVERDUE_RANGES,
    overdueRangesStorage,
    OverdueRanges,
} from "./overdueColumns";
import { useControlHealthDashboardQuery } from "./queries/useControlHealthDashboardQuery";
import { useControlHealthWidgets } from "./useControlHealthWidgets";
import widgetsIcon from "./widgets-empty.svg";

export function ControlHealthContainer({
    isNewDashboard,
}: {
    isNewDashboard: boolean;
}) {
    const widgetView = useControlHealthWidgets(isNewDashboard);
    return (
        <ControlHealthFiltersProvider>
            <div className={theme.root}>
                <ControlHealthFilterPanel
                    headerActions={
                        isNewDashboard && (
                            <ControlHealthAdjustView {...widgetView} />
                        )
                    }
                />
                <ControlHealthWidgetsErrorBoundary>
                    <ControlHealthWidgets
                        {...widgetView}
                        isNewDashboard={isNewDashboard}
                    />
                </ControlHealthWidgetsErrorBoundary>
            </div>
        </ControlHealthFiltersProvider>
    );
}

function ControlHealthWidgets({
    widgets,
    isVisible,
    isNewDashboard,
}: ReturnType<typeof useControlHealthWidgets> & { isNewDashboard: boolean }) {
    const { t } = useTranslation();
    const reducedMotion = useReducedMotion();
    const transition = { duration: reducedMotion ? 0 : 0.2 };
    const hidden = { opacity: 0 };
    const { filters, dateRange } = useControlHealthFilters();
    const [savedOverdueRanges, setOverdueRanges] = useState(() =>
        overdueRangesStorage.getOr("overdueRanges", DEFAULT_OVERDUE_RANGES)
    );
    const overdueRanges = isNewDashboard
        ? savedOverdueRanges
        : DEFAULT_OVERDUE_RANGES;
    const onOverdueRangesChange = (ranges: OverdueRanges) => {
        overdueRangesStorage.set("overdueRanges", ranges);
        setOverdueRanges(ranges);
    };
    const { data: dashboardData, isFetching } =
        useControlHealthDashboardQuery(overdueRanges);
    const isEmpty = !widgets.some((widget) => widget.visible);
    const emptyState = (
        <Section className={theme.empty}>
            <EmptyState
                icon={<img src={widgetsIcon} alt="" />}
                title={t("ControlHealth_NoWidgets_Title")}
                description={t("ControlHealth_NoWidgets_Description")}
            />
        </Section>
    );

    if (dateRange === null) {
        return isEmpty ? emptyState : null;
    }

    if (dashboardData === undefined) {
        if (isEmpty) {
            return emptyState;
        }
        return isFetching ? <PageSpinner /> : null;
    }

    const rangeLabel = formatRangeLabel(
        dateRange.start.format("YYYY-MM-DD"),
        dateRange.end.format("YYYY-MM-DD"),
        filters.dateRangePreset.type
    );

    return (
        <div className={cx(theme.widgets, isFetching && theme.stale)}>
            <AnimatePresence initial={false} mode="popLayout">
                {isEmpty && (
                    <motion.div
                        key="empty"
                        layout={reducedMotion ? false : "position"}
                        initial={hidden}
                        animate={{ opacity: 1 }}
                        exit={hidden}
                        transition={transition}
                    >
                        {emptyState}
                    </motion.div>
                )}
                {widgets.some((widget) => widget.kpi && widget.visible) && (
                    <motion.div
                        key="kpis"
                        layout={reducedMotion ? false : "position"}
                        className={theme.kpiRow}
                        initial={hidden}
                        animate={{ opacity: 1 }}
                        exit={hidden}
                        transition={transition}
                    >
                        <AnimatePresence initial={false} mode="popLayout">
                            {isVisible("onTimeRate") && (
                                <motion.div
                                    key="onTimeRate"
                                    layout={reducedMotion ? false : "position"}
                                    className={theme.kpi}
                                    initial={hidden}
                                    animate={{ opacity: 1 }}
                                    exit={hidden}
                                    transition={transition}
                                >
                                    <ControlHealthOnTimeRateWidget
                                        onTimeRate={
                                            dashboardData.onTimeRate ?? null
                                        }
                                    />
                                </motion.div>
                            )}
                            {isVisible("completionRate") && (
                                <motion.div
                                    key="completionRate"
                                    layout={reducedMotion ? false : "position"}
                                    className={theme.kpi}
                                    initial={hidden}
                                    animate={{ opacity: 1 }}
                                    exit={hidden}
                                    transition={transition}
                                >
                                    <ControlHealthCompletionRateWidget
                                        completionRate={
                                            dashboardData.completionRate ?? null
                                        }
                                    />
                                </motion.div>
                            )}
                            {isVisible("frictionRate") && (
                                <motion.div
                                    key="frictionRate"
                                    layout={reducedMotion ? false : "position"}
                                    className={theme.kpi}
                                    initial={hidden}
                                    animate={{ opacity: 1 }}
                                    exit={hidden}
                                    transition={transition}
                                >
                                    <ControlHealthFrictionRateWidget
                                        frictionRate={
                                            dashboardData.frictionRate ?? null
                                        }
                                    />
                                </motion.div>
                            )}
                            {isVisible("activities") && (
                                <motion.div
                                    key="activities"
                                    layout={reducedMotion ? false : "position"}
                                    className={theme.activities}
                                    initial={hidden}
                                    animate={{ opacity: 1 }}
                                    exit={hidden}
                                    transition={transition}
                                >
                                    <ControlHealthActivitiesWidget
                                        breakdown={
                                            dashboardData.activityBreakdown
                                        }
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                )}
                {isVisible("activityTrend") && (
                    <motion.div
                        key="activityTrend"
                        layout={reducedMotion ? false : "position"}
                        initial={hidden}
                        animate={{ opacity: 1 }}
                        exit={hidden}
                        transition={transition}
                    >
                        <ControlHealthActivityTrendWidget
                            trend={dashboardData.activityTrend}
                            rangeLabel={rangeLabel}
                        />
                    </motion.div>
                )}
                {isVisible("frictionBreakdown") && (
                    <motion.div
                        key="frictionBreakdown"
                        layout={reducedMotion ? false : "position"}
                        initial={hidden}
                        animate={{ opacity: 1 }}
                        exit={hidden}
                        transition={transition}
                    >
                        <ControlHealthFrictionBreakdownWidget
                            breakdown={dashboardData.frictionBreakdown}
                            rangeLabel={rangeLabel}
                        />
                    </motion.div>
                )}
                {isVisible("statusBreakdown") && (
                    <motion.div
                        key="statusBreakdown"
                        layout={reducedMotion ? false : "position"}
                        initial={hidden}
                        animate={{ opacity: 1 }}
                        exit={hidden}
                        transition={transition}
                    >
                        <ControlHealthStatusBreakdownTable
                            isNewDashboard={isNewDashboard}
                            overdueRanges={overdueRanges}
                            onOverdueRangesChange={onOverdueRangesChange}
                            programs={dashboardData.programOverview}
                            rangeLabel={rangeLabel}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
